import React, { useState, useRef } from "react";
import "./AccountPlanTdComp.css";
import axios from "axios";
import { API_BASE_URL } from "../../config/apiConfig";
import api from "../../services/api";

const apiForFile = axios.create({
    baseURL: API_BASE_URL,
});

apiForFile.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
            config.headers = {
                ...config.headers,
                Authorization: `Bearer ${token}`,
            };
        }

        if (config.data instanceof FormData) {
            if (config.headers && config.headers['Content-Type']) {
                delete config.headers['Content-Type'];
            }
        }

        return config;
    },
    (error) => Promise.reject(error)
);

apiForFile.defaults.responseType = 'blob';

function AccountPlanTdComp({
    accountName,
    clientName,
    accountCode,
    clientCode,
    filePath,
    refreshAPI
}) {
    const [modalState, setModalState] = useState({
        isOpen: false,
        file: null,
        isDragging: false,
        error: "",
        success: "",
    });

    const [actionState, setActionState] = useState({
        isUploading: false,
        isDeleting: false,
        showDeleteConfirmation: false,
        showSuccessModal: false,
        successMessage: "",
    });

    const fileInputRef = useRef(null);

    const updateModalState = (updates) =>
        setModalState((prev) => ({ ...prev, ...updates }));

    const updateActionState = (updates) =>
        setActionState((prev) => ({ ...prev, ...updates }));

    const openModal = () =>
        updateModalState({
            isOpen: true,
            file: null,
            error: "",
            success: "",
        });

    const closeModal = () => updateModalState({ isOpen: false });

    const handleShowDeleteConfirmation = () =>
        updateActionState({ showDeleteConfirmation: true });

    const hideDeleteConfirmation = () =>
        updateActionState({ showDeleteConfirmation: false });

    const showUploadSuccessModal = (message) => {
        updateActionState({ 
            showSuccessModal: true,
            successMessage: message
        });
    };

    const hideSuccessModal = () => {
        updateActionState({ 
            showSuccessModal: false,
            successMessage: "" 
        });
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        updateModalState({ isDragging: true });
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        updateModalState({ isDragging: false });
    };

    const handleDragOver = (e) => e.preventDefault();

    const handleDrop = (e) => {
        e.preventDefault();
        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            updateModalState({
                file: droppedFile,
                isDragging: false,
            });
        }
    };

    const handleFileInputChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            updateModalState({
                file: selectedFile,
            });
        }
    };

    const triggerFileInput = () => fileInputRef.current.click();

    const validateInputs = () => {
        if (!accountCode || !clientCode) {
            return "Account code and client code are required";
        }
        return null;
    };

    const handleApiError = (error, defaultMessage) => {
        const errorMessage = error.response?.data?.error || defaultMessage;
        updateModalState({ error: errorMessage });
    };

    const handleUpload = async () => {
        const { file } = modalState;

        if (!file) {
            updateModalState({ error: "Please select a file" });
            return;
        }

        const validationError = validateInputs();
        if (validationError) {
            updateModalState({ error: validationError });
            return;
        }

        try {
            updateActionState({ isUploading: true });
            updateModalState({ error: "", success: "" });

            const formData = new FormData();
            formData.append("file", file);
            formData.append("account_code", accountCode);
            formData.append("client_code", clientCode);

            const response = await api.post(
                "/accounts/addOrUpdateAccountPlan",
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );

            const successMessage = response.data.message || "File uploaded successfully";
            updateModalState({ success: successMessage });
            showUploadSuccessModal(successMessage);
            
            closeModal();
            refreshAPI();
        } catch (error) {
            handleApiError(error, "Failed to upload file");
        } finally {
            updateActionState({ isUploading: false });
        }
    };

    const handleDelete = async () => {
        const validationError = validateInputs();
        if (validationError) {
            updateModalState({ error: validationError });
            hideDeleteConfirmation();
            return;
        }

        try {
            updateActionState({ isDeleting: true });
            updateModalState({ error: "", success: "" });

            const formData = new FormData();
            formData.append("account_code", accountCode);
            formData.append("client_code", clientCode);

            const response = await api.post(
                "/accounts/deleteAccountPlan",
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );

            updateModalState({
                success: response.data.message || "File deleted successfully",
            });

            setTimeout(() => {
                hideDeleteConfirmation();
                refreshAPI();
            }, 0);
        } catch (error) {
            handleApiError(error, "Failed to delete file");
            hideDeleteConfirmation();
        } finally {
            updateActionState({ isDeleting: false });
        }
    };

    const handleAccountPlanDownload = async () => {
        const validationError = validateInputs();
        if (validationError) {
            updateModalState({ error: validationError });
            return;
        }
    
        try {
            const formData = new FormData();
            formData.append("account_code", accountCode);
            formData.append("client_code", clientCode);
    
            const response = await apiForFile.post(
                "/accounts/downloadAccountPlan",
                formData
            );
    
            const contentDisposition = response.headers['content-disposition'];
            let filename = "account_plan.xlsx";
    
            if (contentDisposition) {
                const filenameRegex = /filename\*?=['"]?(?:UTF-\d['"]*)?([^;\r\n"']*)['"]?;?/gi;
                const matches = filenameRegex.exec(contentDisposition);
                if (matches && matches[1]) {
                    filename = matches[1]
                        .replace(/^utf-8''/i, '')
                        .replace(/%20/g, ' ');
                }
            }
    
            const contentType = response.headers['content-type'];
            let blobType = contentType;
    
            if (!blobType || blobType === 'application/octet-stream') {
                const ext = filename.split('.').pop().toLowerCase();
                blobType = {
                    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    xls: 'application/vnd.ms-excel',
                    pdf: 'application/pdf',
                }[ext] || 'application/octet-stream';
            }
    
            const blob = new Blob([response.data], { type: blobType });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
    
            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }, 100);
    
        } catch (error) {
            handleApiError(error, "Failed to download file");
        }
    };

    const { isOpen, file, isDragging, error, success } = modalState;
    const { 
        isUploading, 
        isDeleting, 
        showDeleteConfirmation, 
        showSuccessModal, 
        successMessage 
    } = actionState;

    return (
        <div className="account-plan-td-container">
            <div className="account-plan-action-buttons">
                <button className="btn-sm btn btn-outline font12" onClick={openModal}>
                    Upload
                </button>

                {filePath && (
                    <>
                        <button
                            className="account-plan-btn account-plan-btn-delete"
                            onClick={handleShowDeleteConfirmation}
                            disabled={isDeleting}
                        >
                            {isDeleting ? (
                                <>
                                    <span className="account-plan-spinner account-plan-spinner-small"></span> Deleting...
                                </>
                            ) : (
                                "Delete"
                            )}
                        </button>
                        <button className="account-plan-btn account-plan-btn-view" onClick={handleAccountPlanDownload}>
                            Download
                        </button>
                    </>
                )}
            </div>

            {isOpen && (
                <div className="account-plan-modal-overlay">
                    <div className="account-plan-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="account-plan-modal-header">
                            <h3 className="account-plan-modal-title">Upload Account Plan</h3>
                            <button className="account-plan-modal-close" onClick={closeModal}>
                                &times;
                            </button>
                        </div>

                        <div className="account-plan-info">
                            <div className="account-plan-info-row">
                                <span className="account-plan-info-label">Account Name:</span>
                                <span className="account-plan-info-value">{accountName}</span>
                            </div>
                            <div className="account-plan-info-row">
                                <span className="account-plan-info-label">Client Name:</span>
                                <span className="account-plan-info-value">{clientName}</span>
                            </div>
                            <div className="account-plan-info-row">
                                <span className="account-plan-info-label">Account Code:</span>
                                <span className="account-plan-info-value">{accountCode}</span>
                            </div>
                            <div className="account-plan-info-row">
                                <span className="account-plan-info-label">Client Code:</span>
                                <span className="account-plan-info-value">{clientCode}</span>
                            </div>
                        </div>

                        <div
                            className={`account-plan-dropzone ${isDragging ? "account-plan-dragging" : ""}`}
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            onClick={triggerFileInput}
                        >
                            {file ? (
                                <p className="account-plan-file-selected">Selected file: {file.name}</p>
                            ) : (
                                <p className="account-plan-file-prompt">
                                    <i className="account-plan-upload-icon">↑</i> Drag & drop a file here or
                                    click to select
                                </p>
                            )}
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileInputChange}
                                style={{ display: "none" }}
                                accept=".pdf,.doc,.docx,.xls,.xlsx"
                            />
                        </div>

                        {error && (
                            <div className="account-plan-message account-plan-message-error">
                                <i className="account-plan-icon">⚠️</i> {error}
                            </div>
                        )}
                        {success && (
                            <div className="account-plan-message account-plan-message-success">
                                <i className="account-plan-icon">✓</i> {success}
                            </div>
                        )}

                        <div className="account-plan-modal-footer">
                            <button
                                className="account-plan-btn account-plan-btn-confirm"
                                onClick={handleUpload}
                                disabled={!file || isUploading}
                            >
                                {isUploading ? (
                                    <>
                                        <span className="account-plan-spinner"></span> Uploading...
                                    </>
                                ) : (
                                    "Upload"
                                )}
                            </button>
                            <button
                                className="account-plan-btn account-plan-btn-cancel"
                                onClick={closeModal}
                                disabled={isUploading}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showDeleteConfirmation && (
                <div className="account-plan-modal-overlay" onClick={hideDeleteConfirmation}>
                    <div
                        className="account-plan-modal-content account-plan-modal-content-small"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="account-plan-modal-header">
                            <h3 className="account-plan-modal-title">Confirm Deletion</h3>
                            <button className="account-plan-modal-close" onClick={hideDeleteConfirmation}>
                                &times;
                            </button>
                        </div>

                        <div className="account-plan-confirmation-content">
                            <p>Are you sure you want to delete this account plan file?</p>
                            <p className="account-plan-warning-text">This action cannot be undone.</p>
                        </div>

                        <div className="account-plan-modal-footer">
                            <button
                                className="account-plan-btn account-plan-btn-danger"
                                onClick={handleDelete}
                                disabled={isDeleting}
                            >
                                {isDeleting ? (
                                    <>
                                        <span className="account-plan-spinner"></span> Deleting...
                                    </>
                                ) : (
                                    "Delete"
                                )}
                            </button>
                            <button
                                className="account-plan-btn account-plan-btn-secondary"
                                onClick={hideDeleteConfirmation}
                                disabled={isDeleting}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showSuccessModal && (
                <div className="account-plan-modal-overlay" onClick={hideSuccessModal}>
                    <div
                        className="account-plan-modal-content account-plan-modal-content-small"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="account-plan-modal-header">
                            <h3 className="account-plan-modal-title">Success</h3>
                            <button className="account-plan-modal-close" onClick={hideSuccessModal}>
                                &times;
                            </button>
                        </div>

                        <div className="account-plan-confirmation-content">
                            <p>{successMessage}</p>
                        </div>

                        <div className="account-plan-modal-footer">
                            <button
                                className="account-plan-btn account-plan-btn-confirm"
                                onClick={hideSuccessModal}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AccountPlanTdComp;